/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Crown, Check, Loader2, ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PremiumTemplate, AssignedPremiumTemplate } from '@/types/superadmin.types';
import { useAuthStore } from '@/stores/authStore';

interface AssignTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (code: string) => void;
  isAssigning: boolean;
  assignableTemplates: PremiumTemplate[];
  assignedTemplates: AssignedPremiumTemplate[];
}

/** Batch-fetch preview URLs for multiple template codes */
function useAdminPreviewUrls(codes: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const revokeRef = useRef<string[]>([]);

  useEffect(() => {
    if (codes.length === 0) return;
    let cancelled = false;

    Promise.all(
      codes.map(async (code) => {
        try {
          const res = await fetch(`/api/v1/admin/organizations/invoice-templates/${encodeURIComponent(code)}/preview`, {
            headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken || ''}` },
          });
          if (!res.ok) return [code, null] as const;
          const blob = await res.blob();
          if (blob.size === 0) return [code, null] as const;
          return [code, URL.createObjectURL(blob)] as const;
        } catch {
          return [code, null] as const;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      results.forEach(([code, url]) => {
        if (url) {
          map[code] = url;
          revokeRef.current.push(url);
        }
      });
      setUrls(map);
    });

    return () => {
      cancelled = true;
      revokeRef.current.forEach((u) => URL.revokeObjectURL(u));
      revokeRef.current = [];
    };
  }, [codes.join(',')]);

  return urls;
}

const AssignTemplateModal: React.FC<AssignTemplateModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  isAssigning,
  assignableTemplates,
  assignedTemplates,
}) => {
  const { t } = useTranslation('platform');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const assignedCodes = new Set(assignedTemplates.map((t) => t.code));
  const available = isOpen ? assignableTemplates.filter((t) => !assignedCodes.has(t.code)) : [];
  const codes = available.map((t) => t.code);
  const previewUrls = useAdminPreviewUrls(isOpen ? codes : []);

  if (!isOpen) return null;

  const handleAssign = () => {
    if (selectedCode) {
      onAssign(selectedCode);
      setSelectedCode(null);
    }
  };

  const handleClose = () => {
    setSelectedCode(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              {t('organizationDetail.premiumTemplates.assignModal.title')}
            </h2>
            <p className="text-sm text-secondary mt-0.5">
              {t('organizationDetail.premiumTemplates.assignModal.subtitle')}
            </p>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-background text-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {available.length === 0 ? (
            <p className="text-secondary text-sm text-center py-8">
              {t('organizationDetail.premiumTemplates.assignModal.noTemplates')}
            </p>
          ) : (
            <div className="space-y-3">
              {available.map((template) => (
                <button
                  key={template.code}
                  onClick={() => setSelectedCode(template.code)}
                  className={`w-full flex items-start gap-4 p-3 rounded-xl border transition-all text-left ${
                    selectedCode === template.code
                      ? 'border-amber-500 bg-amber-500/10 ring-1 ring-amber-500/30'
                      : 'border-border hover:border-amber-500/40 hover:bg-background'
                  }`}
                >
                  <div className="w-16 h-20 rounded-lg border border-border flex-shrink-0 overflow-hidden bg-background flex items-center justify-center">
                    {previewUrls[template.code] ? (
                      <img
                        src={previewUrls[template.code]}
                        alt={template.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-secondary/40" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <span className="font-medium text-foreground">{template.name}</span>
                      {selectedCode === template.code && (
                        <Check className="w-4 h-4 text-amber-500" />
                      )}
                    </div>
                    <p className="text-sm text-secondary mt-0.5 line-clamp-2">{template.description}</p>
                    <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-500 rounded-full">
                      Premium
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {available.length > 0 && (
          <div className="flex justify-end gap-3 p-5 border-t border-border">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-secondary hover:text-foreground rounded-lg hover:bg-background transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedCode || isAssigning}
              className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isAssigning && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('organizationDetail.premiumTemplates.assign')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { useAdminPreviewUrls };
export default AssignTemplateModal;
